import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { showInkToast } from '../cms/InkToast';
import { PasswordInput } from '../common/PasswordInput';

/**
 * Reach → Channels → Add a channel → Bluesky (UX optimization review,
 * 2026-10-24). Bluesky has no sign-in window: the person gives their handle
 * and an app password made in Bluesky, which they can revoke there at any
 * time. The server checks both with Bluesky before the channel is saved
 * (connectBlueskyChannel); the password is never shown again.
 */

const CONNECT_BLUESKY = gql`
  mutation ConnectBlueskyChannel($accountId: ID!, $handle: String!, $appPassword: String!) {
    connectBlueskyChannel(accountId: $accountId, handle: $handle, appPassword: $appPassword) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
    }
  }
`;

export function BlueskyConnect({
  accountId,
  initialHandle = '',
  reconnect = false,
  onClose,
  onConnected,
}: {
  accountId: string;
  /** Set when reconnecting a channel already on the list. */
  initialHandle?: string;
  reconnect?: boolean;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [handle, setHandle] = useState(initialHandle);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [connect, { loading }] = useMutation(CONNECT_BLUESKY);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await connect({
        variables: { accountId, handle: handle.trim(), appPassword: password.trim() },
      });
      showInkToast(reconnect ? 'Bluesky reconnected.' : 'Bluesky connected.');
      onConnected();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  return (
    <div className="ink-modal-back" role="dialog" aria-modal="true" aria-labelledby="bsky-title">
      <form className="ink-modal bsky" onSubmit={(e) => void submit(e)}>
        <div className="ink-modal-title" id="bsky-title">
          {reconnect ? 'Reconnect Bluesky' : 'Connect Bluesky'}
        </div>
        <p className="ink-modal-copy">
          Bluesky uses an app password instead of a sign-in window. Make one in Bluesky under{' '}
          <a href="https://bsky.app/settings/app-passwords" target="_blank" rel="noreferrer">
            Settings → Privacy and security → App passwords
          </a>
          , then paste it here. You can revoke it there any time.
        </p>
        <label className="bsky-field">
          <span>Handle</span>
          <input
            className="ink-site-input"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="yourname.bsky.social"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            disabled={loading}
            autoFocus
          />
        </label>
        <label className="bsky-field">
          <span>App password</span>
          <PasswordInput
            className="ink-site-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="App password"
            autoComplete="off"
            spellCheck={false}
            disabled={loading}
          />
        </label>
        {error ? (
          <p className="bsky-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="ink-modal-actions">
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            disabled={loading}
            onClick={onClose}
          >
            Not now
          </button>
          <button
            type="submit"
            className="ink-btn ink-btn-primary"
            disabled={loading || !handle.trim() || !password.trim()}
          >
            {loading ? 'Checking with Bluesky…' : reconnect ? 'Reconnect' : 'Connect'}
          </button>
        </div>
      </form>
    </div>
  );
}
